import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {catchError, tap} from 'rxjs/operators';
import {throwError, Subject, BehaviorSubject} from 'rxjs';

import {Router} from '@angular/router';
import {User, UserCreate, UserLogin} from '../shared/models/user.model';

export interface AuthResponseData {
    kind: string;
    idToken: string;
    user_name: string;
    level: number;
    refreshToken: string;
    expiresIn: string;
    localId: number;
    registered?: boolean;
}

@Injectable({providedIn: 'root'})
export class AuthenticationService {
    // next를 새로 안받아도 값을 리턴할 수 있어야한다.
    user = new BehaviorSubject<UserLogin>(null);
    private tokenExpirationTimer: any;

    constructor(private http: HttpClient, private router: Router) {
    }

    signup(user: UserCreate) {
        return this.http.post<AuthResponseData>('https://server.ozshuttle.com/api/user/create.php', user)
            .pipe(
                catchError(this.handleError),
                tap(resData => {
                    this.handleAuthentication(
                        resData.user_name,
                        resData.localId,
                        resData.level,
                        resData.idToken,
                        +resData.expiresIn
                    );
                })
            );
    }

    login(user_name: string, password: string) {
        return this.http
            .post<AuthResponseData>(
                'https://server.ozshuttle.com/api/user/login.php',
                {
                    user_name: user_name,
                    password: password
                }
            )
            .pipe(
                catchError(this.handleError),
                tap(resData => {
                    this.handleAuthentication(
                        resData.user_name,
                        resData.localId,
                        resData.level,
                        resData.idToken,
                        +resData.expiresIn
                    );
                })
            );
    }

    autoLogin() {
        const userData: {
            user_name: string;
            id: number;
            level: number;
            _token: string;
            _tokenExpirationDate: string;
        } = JSON.parse(localStorage.getItem('userData'));
        if (!userData) {
            return;
        }

// Same coercion as handleAuthentication: localStorage round-trip preserves
// the original string types, so we normalize here too.
        const loadedUser = new UserLogin(
            userData.user_name,
            +userData.id,
            +userData.level,
            userData._token,
            new Date(userData._tokenExpirationDate)
        );

        if (loadedUser.token) {
            this.user.next(loadedUser);
            const expirationDuration =
                new Date(userData._tokenExpirationDate).getTime() -
                new Date().getTime();
            this.autoLogout(expirationDuration);
        }
    }

    logout() {
        this.user.next(null);
        this.router.navigate(['authentication/sign-in']);
        localStorage.removeItem('userData');
        if (this.tokenExpirationTimer) {
            clearTimeout(this.tokenExpirationTimer);
        }
        this.tokenExpirationTimer = null;
    }

    autoLogout(expirationDuration: number) {
        this.tokenExpirationTimer = setTimeout(() => {
            this.logout();
        }, expirationDuration);
    }


    private handleAuthentication(
        user_name: string,
        userId: number,
        level: number,
        token: string,
        expiresIn: number
    ) {
        const expirationDate = new Date(new Date().getTime() + expiresIn * 2000);

        // Backend returns id and level as strings (e.g. "159", "2"). Coerce
        // to numbers once at the source so strict equality (=== 2) works
        // everywhere downstream. Without this, expressions like
        // `user.level === 2` silently fail and trigger the wrong code path.
        const user = new UserLogin(
            user_name,
            +userId,
            +level,
            token,
            expirationDate
        );

        this.user.next(user);
        this.autoLogout(expiresIn * 2000);
        localStorage.setItem('userData', JSON.stringify(user));
    }

    private handleError(errorRes: HttpErrorResponse) {
        console.log(errorRes);

        let errorMessage = 'An unknown error occurred!';
        if (!errorRes.error || !errorRes.error.message) {
            return throwError(errorMessage);
        }
        switch (errorRes.error.message) {
            case 'USER_NAME_EXISTS':
                errorMessage = 'This username exists already';
                break;
            case 'EMAIL_NOT_FOUND':
                errorMessage = 'This email does not exist.';
                break;
            case 'INVALID_LOGIN':
                errorMessage = 'Your password or username is not correct.';
                break;
        }
        return throwError(errorMessage);
    }

    resetPassword(password, selector, validator) {
        // add backend to actually update.
        return this.http
            .post<any>(
                'https://server.ozshuttle.com/api/user/update_password.php',
                {
                    password: password,
                    selector: selector,
                    validator: validator
                }
            )
            .pipe(
                catchError(this.handleError)
            );
    }

    updateLastLoggedIn(user_name: string) {
        return this.http
            .post<any>(
                'https://server.ozshuttle.com/api/user/update_last_loggedin.php',
                {
                    user_name: user_name
                }
            )
            .pipe(
                catchError(this.handleError)
            );
    }
}
